<script setup lang="ts">
import CurrencyText from '@uiKit/components/CurrencyText.vue'
import Box from '@uiKit/elements/Box.vue'
import Text from '@uiKit/elements/Text.vue'
import { DiscountBadgeIcon } from '@uiKit/icons'
import InfoSheetButton from '@uiKit/components/InfoSheetButton.vue'
import type { OfferingInfoTile } from '../constants/offeringInfo'

// One figure on a property card. Value, caption and ⓘ explainer all come
// from the offeringInfo builders; no sheet → no icon.

interface Props {
  label: string
  tile: OfferingInfoTile
}

defineProps<Props>()
</script>

<template>
  <Box direction="col" gap="6px" class="metric-tile">
    <!-- Figma: 14px bold label. Wraps rather than truncates on the narrowest
         cards so it's never cut off. -->
    <Box align="start" justify="between" gap="2">
      <Text
        type="body"
        weight="bold"
        color="var(--p-slate-400)"
        transform="uppercase"
        class="metric-tile__label"
        style="line-height: normal"
      >
        {{ label }}
      </Text>
      <InfoSheetButton :content="tile.sheet" :label="tile.sheet.title" />
    </Box>
    <!-- heading (16px) rather than 18px: a 7-digit AED price must fit a
         half-width tile at every grid breakpoint without truncating. -->
    <Text is="span" type="heading" truncate><CurrencyText :text="tile.value || '-'" /></Text>
    <Box v-if="tile.caption" align="center" gap="1">
      <DiscountBadgeIcon v-if="tile.isDiscountCaption" class="metric-tile__discount-icon" />
      <Text
        is="span"
        type="body"
        weight="medium"
        :color="tile.isDiscountCaption ? 'var(--p-emerald-600)' : 'var(--p-slate-400)'"
        truncate
        style="line-height: normal"
      >
        <CurrencyText :text="tile.caption" />
      </Text>
    </Box>
  </Box>
</template>

<style scoped>
.metric-tile {
  min-width: 0;
  padding: 12px;
  border-radius: 16px;
  border: 1px solid var(--p-primary-100);
  background: var(--p-surface-0);
}

.metric-tile__label {
  min-width: 0;
  overflow-wrap: anywhere;
}

.metric-tile__discount-icon {
  flex-shrink: 0;
  color: var(--p-emerald-600);
}
</style>
